import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface BlogNewsCardProps {
  title: string;
  summary: string;
  imageUrl: string;
  articleUrl: string;
  category: string;
  publishedDate: string;
  readTime?: string;
  index?: number;
}

/**
 * Verifica se a notícia foi publicada hoje
 */
function isPublishedToday(publishedDate: string): boolean {
  const today = new Date().toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
  return publishedDate === today;
}

const BlogNewsCard: React.FC<BlogNewsCardProps> = ({
  title,
  summary,
  imageUrl,
  articleUrl,
  category,
  publishedDate,
  readTime = '5 min',
  index = 0
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const categoryColorsDark: Record<string, string> = {
    'Investimentos': 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    'Economia': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
    'Finanças Pessoais': 'bg-purple-500/20 text-purple-300 border-purple-500/50',
    'Notícias': 'bg-orange-500/20 text-orange-300 border-orange-500/50',
    'Dicas': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50'
  };

  const categoryColorsLight: Record<string, string> = {
    'Investimentos': 'bg-blue-100 text-blue-700 border-blue-300',
    'Economia': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Finanças Pessoais': 'bg-purple-100 text-purple-700 border-purple-300',
    'Notícias': 'bg-orange-100 text-orange-700 border-orange-300',
    'Dicas': 'bg-yellow-100 text-yellow-700 border-yellow-300'
  };

  const categoryColors = isDark ? categoryColorsDark : categoryColorsLight;
  const categoryStyle = categoryColors[category] || (isDark ? 'bg-slate-500/20 text-slate-300 border-slate-500/50' : 'bg-slate-100 text-slate-700 border-slate-300');

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`group relative rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-xl ${
        isDark 
          ? 'bg-slate-800/50 backdrop-blur-sm border-slate-700/50 hover:border-slate-600/80 hover:shadow-slate-900/50'
          : 'bg-white border-teal-200 hover:border-teal-300 hover:shadow-teal-100/50'
      }`}
    >
      {/* Imagem */}
      <div className={`relative h-48 overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
        <img 
          src={imageUrl} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent'
            : 'bg-gradient-to-t from-white/80 via-white/10 to-transparent'
        }`} />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 right-3 flex items-center gap-2">
          <div className={`px-3 py-1 rounded-full text-xs font-bold border ${categoryStyle} backdrop-blur-sm`}>
            {category}
          </div>
          
          {/* Badge de NOVA para artigos de hoje */}
          {isPublishedToday(publishedDate) && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="px-3 py-1 rounded-full text-xs font-black bg-gradient-to-r from-yellow-500 to-orange-500 text-white flex items-center gap-1 shadow-lg"
            >
              <Sparkles size={12} />
              NOVA
            </motion.div>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-5">
        <h3 className={`text-lg font-bold mb-2 line-clamp-2 transition-colors ${
          isDark 
            ? 'text-white group-hover:text-indigo-400'
            : 'text-slate-900 group-hover:text-teal-600'
        }`}>
          {title}
        </h3>
        
        <p className={`text-sm mb-4 line-clamp-3 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {summary}
        </p>

        {/* Meta Info */}
        <div className={`flex items-center justify-between text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {publishedDate}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {readTime} leitura
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className={`text-sm transition-colors cursor-pointer ${
          isDark 
            ? 'text-slate-500 hover:text-indigo-400'
            : 'text-slate-600 hover:text-teal-600'
        }`}>
          Clique para ler o resumo →
        </div>
      </div>
    </motion.article>
  );
};

export default BlogNewsCard;
